import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Eye, EyeOff, Mail, Lock, User, ArrowRight, Loader2 } from 'lucide-react';
import { motion } from 'framer-motion';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatApiError(detail) {
  if (detail == null) return 'Something went wrong. Please try again.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map(e => e?.msg || JSON.stringify(e)).filter(Boolean).join(' ');
  if (detail?.msg) return detail.msg;
  return String(detail);
}

export default function Login() {
  const [mode, setMode] = useState('register');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      if (mode === 'register') {
        const { data } = await axios.post(`${API}/auth/register`, { email, password, name }, { withCredentials: true });
        login(data.user);
        navigate('/onboarding');
      } else {
        const { data } = await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true });
        login(data.user);
        navigate(data.business ? '/dashboard' : '/onboarding');
      }
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleAuth = () => {
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4" style={{ backgroundColor: '#EDE8DF' }} data-testid="login-page">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-[420px]"
      >
        <div className="text-center mb-8">
          <Link to="/" className="inline-block mb-6">
            <span className="text-[15px] font-semibold" style={{ fontFamily: "'DM Sans', sans-serif", color: '#3C3226' }}>GetChosenFirst</span>
          </Link>
          <h1 className="text-2xl font-medium" style={{ fontFamily: "'Playfair Display', serif", color: '#3C3226' }} data-testid="auth-heading">
            {mode === 'login' ? 'Welcome back' : 'Create your account'}
          </h1>
          <p className="text-[13px] mt-2" style={{ color: '#5C5347' }}>
            {mode === 'login' ? 'Sign in to your dashboard' : 'Get started with your free AI scan'}
          </p>
        </div>

        <div className="p-6" style={{ border: '1px solid #D4C9B8', backgroundColor: '#F7F4EF', borderRadius: '2px' }}>
          <form onSubmit={handleSubmit} className="space-y-4">
            {mode === 'register' && (
              <div>
                <label className="block text-[11px] font-medium mb-1.5" style={{ color: '#5C5347', letterSpacing: '0.05em', textTransform: 'uppercase' }}>Full Name</label>
                <div className="relative">
                  <User size={16} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: '#8C7E6E' }} />
                  <input
                    type="text"
                    value={name}
                    onChange={e => setName(e.target.value)}
                    placeholder="John Doe"
                    className="w-full pl-10 pr-4 py-2.5 rounded-none text-sm outline-none transition-all"
                    style={{ border: '1px solid #D4C9B8', color: '#3C3226', backgroundColor: '#FDFBF7', borderRadius: 0 }}
                    onFocus={e => e.target.style.borderColor = '#B8985A'}
                    onBlur={e => e.target.style.borderColor = '#D4C9B8'}
                    data-testid="register-name-input"
                  />
                </div>
              </div>
            )}

            <div>
              <label className="block text-[11px] font-medium mb-1.5" style={{ color: '#5C5347', letterSpacing: '0.05em', textTransform: 'uppercase' }}>Email</label>
              <div className="relative">
                <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: '#8C7E6E' }} />
                <input
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  required
                  className="w-full pl-10 pr-4 py-2.5 rounded-none text-sm outline-none transition-all"
                  style={{ border: '1px solid #D4C9B8', color: '#3C3226', backgroundColor: '#FDFBF7', borderRadius: 0 }}
                  onFocus={e => e.target.style.borderColor = '#B8985A'}
                  onBlur={e => e.target.style.borderColor = '#D4C9B8'}
                  data-testid="email-input"
                />
              </div>
            </div>

            <div>
              <label className="block text-[11px] font-medium mb-1.5" style={{ color: '#5C5347', letterSpacing: '0.05em', textTransform: 'uppercase' }}>Password</label>
              <div className="relative">
                <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: '#8C7E6E' }} />
                <input
                  type={showPw ? 'text' : 'password'}
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder={mode === 'register' ? 'Min. 6 characters' : 'Your password'}
                  required
                  className="w-full pl-10 pr-10 py-2.5 rounded-none text-sm outline-none transition-all"
                  style={{ border: '1px solid #D4C9B8', color: '#3C3226', backgroundColor: '#FDFBF7', borderRadius: 0 }}
                  onFocus={e => e.target.style.borderColor = '#B8985A'}
                  onBlur={e => e.target.style.borderColor = '#D4C9B8'}
                  data-testid="password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPw(!showPw)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 transition-colors"
                  style={{ color: '#8C7E6E' }}
                  data-testid="toggle-password-btn"
                >
                  {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            {error && (
              <div className="p-3 rounded-lg text-xs font-medium" style={{ backgroundColor: 'rgba(212,68,68,0.06)', color: '#D44' }} data-testid="auth-error">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-none text-[#F0EBE3] font-medium text-sm transition-opacity hover:opacity-85 disabled:opacity-50"
              style={{ backgroundColor: '#2C2520', borderRadius: 0 }}
              data-testid="auth-submit-btn"
            >
              {loading ? (
                <Loader2 size={16} className="animate-spin" />
              ) : (
                <>
                  {mode === 'login' ? 'Sign In' : 'Create Account'}
                  <ArrowRight size={16} />
                </>
              )}
            </button>
          </form>

          <div className="flex items-center gap-3 my-4">
            <div className="flex-1 h-px" style={{ backgroundColor: '#D4C9B8' }} />
            <span className="text-[11px] font-medium" style={{ color: '#8C7E6E' }}>or</span>
            <div className="flex-1 h-px" style={{ backgroundColor: '#D4C9B8' }} />
          </div>

          <button
            onClick={handleGoogleAuth}
            className="w-full flex items-center justify-center gap-3 px-4 py-3 rounded-none transition-colors text-sm font-medium hover:bg-[#EDE8DF]"
            style={{ border: '1px solid #D4C9B8', color: '#3C3226', borderRadius: 0 }}
            data-testid="google-login-btn"
          >
            <svg width="18" height="18" viewBox="0 0 24 24">
              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
            </svg>
            Continue with Google
          </button>
        </div>

        <p className="text-center text-[13px] mt-5" style={{ color: '#5C5347' }}>
          {mode === 'login' ? (
            <>Don&apos;t have an account?{' '}
              <button onClick={() => { setMode('register'); setError(''); }} className="font-medium hover:underline" style={{ color: '#3C3226' }} data-testid="switch-to-register">Create one</button>
            </>
          ) : (
            <>Already have an account?{' '}
              <button onClick={() => { setMode('login'); setError(''); }} className="font-medium hover:underline" style={{ color: '#3C3226' }} data-testid="switch-to-login">Sign in</button>
            </>
          )}
        </p>

        <div className="mt-6 p-4 rounded-xl" style={{ backgroundColor: '#F7F4EF', border: '1px solid #D4C9B8' }}>
          <p className="text-[11px] font-medium mb-2" style={{ color: '#C67A5C', letterSpacing: '0.1em', textTransform: 'uppercase' }}>Demo Accounts</p>
          <div className="space-y-1 text-[12px]" style={{ color: '#5C5347' }}>
            <p><span className="font-medium" style={{ color: '#3C3226' }}>Admin:</span> admin@getchosenfirst.com / admin123</p>
            <p><span className="font-medium" style={{ color: '#3C3226' }}>Plumber:</span> mike@example.com / demo1234</p>
            <p><span className="font-medium" style={{ color: '#3C3226' }}>Dentist:</span> sarah@example.com / demo1234</p>
            <p><span className="font-medium" style={{ color: '#3C3226' }}>Photographer:</span> alex@example.com / demo1234</p>
            <p><span className="font-medium" style={{ color: '#3C3226' }}>Trainer:</span> lisa@example.com / demo1234</p>
          </div>
        </div>
      </motion.div>
    </div>
  );
}
